// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { Color } from '../../../Graphics';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export class DMI extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Directional Movement Index';
        this.Comments = 'Smoothed +DI and -DI lines derived from directional movement and true range';
        this.SetIndicatorLine('+DI', Color.Blue, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('-DI', Color.Red, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', 'Type of Moving Average', 0, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { 'Linear Weighted': MAMode.LWMA }
        ]));
        this.MAPeriod = 14;
        super.InputParameter(new InputParameterInteger('MAPeriod', 'Period of Moving Average', 1, 1, 9999));
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, this.MAPeriod);
        this.SetIndexDrawBegin(1, this.MAPeriod);
    }
    GetIndicatorShortName() {
        return 'DMI(' + this.MAPeriod + ')';
    }
    getCustomName() {
        return 'DMI (' + this.ProjectName + ')';
    }
    createInternalIndicators() {
        this.trMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getTrueRange.bind(this), this.MAPeriod, this.MAType, 0, 1);
        this.dmPlusMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getDmPlus.bind(this), this.MAPeriod, this.MAType, 0, 1);
        this.dmMinusMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getDmMinus.bind(this), this.MAPeriod, this.MAType, 0, 1);
        return [this.trMa, this.dmPlusMa, this.dmMinusMa];
    }
    getTrueRange(index) {
        const cd = this.CurrentData;
        const hi = cd.getPriceByType(PriceType.High, index);
        const lo = cd.getPriceByType(PriceType.Low, index);
        const prevClose = cd.Count > index + 1
            ? cd.getPriceByType(PriceType.Close, index + 1)
            : cd.getPriceByType(PriceType.Close, index);
        const tr1 = hi - lo;
        const tr2 = Math.abs(prevClose - hi);
        const tr3 = Math.abs(prevClose - lo);
        return Math.max(tr1, Math.max(tr2, tr3));
    }
    getDmPlus(index) {
        const cd = this.CurrentData;
        if (index + 1 >= cd.Count)
            return 0;
        const upMove = cd.getPriceByType(PriceType.High, index) - cd.getPriceByType(PriceType.High, index + 1);
        const downMove = cd.getPriceByType(PriceType.Low, index + 1) - cd.getPriceByType(PriceType.Low, index);
        return upMove > downMove && upMove > 0 ? upMove : 0;
    }
    getDmMinus(index) {
        const cd = this.CurrentData;
        if (index + 1 >= cd.Count)
            return 0;
        const upMove = cd.getPriceByType(PriceType.High, index) - cd.getPriceByType(PriceType.High, index + 1);
        const downMove = cd.getPriceByType(PriceType.Low, index + 1) - cd.getPriceByType(PriceType.Low, index);
        return downMove > upMove && downMove > 0 ? downMove : 0;
    }
    getDI(index) {
        const smTR = this.trMa.GetValue(index);
        if (!smTR)
            return { plusDI: 0, minusDI: 0 };
        const smPlus = this.dmPlusMa.GetValue(index);
        const smMinus = this.dmMinusMa.GetValue(index);
        const plusDI = (100 * smPlus) / smTR;
        const minusDI = (100 * smMinus) / smTR;
        return { plusDI, minusDI };
    }
    OnQuote() {
        super.OnQuote();
        const count = this.CurrentData.Count;
        if (count <= this.MAPeriod) {
            this.SetValue(0, 0, 0);
            this.SetValue(1, 0, 0);
            return;
        }
        const { plusDI, minusDI } = this.getDI(0);
        this.SetValue(0, 0, plusDI);
        this.SetValue(1, 0, minusDI);
    }
}
